import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { extractErrorMessage } from '../../core/services/api';
import type { UserSource } from '../../core/services/auth.service';

/** Handoff from LoginPage: whatever email the visitor had already typed there. */
export interface RequestAccessState {
  email?: string;
}

const SOURCE_OPTIONS: { value: UserSource; label: string }[] = [
  { value: 'GOOGLE_SEARCH', label: 'Searching on Google' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'TWITTER_X', label: 'X / Twitter' },
  { value: 'FRIEND_REFERRAL', label: 'A friend or colleague' },
  { value: 'BLOG_ARTICLE', label: 'A blog or article' },
  { value: 'CONFERENCE_EVENT', label: 'A conference or event' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Self-serve "ask for access" form, reached from LoginPage for visitors this
 * invite-only system doesn't recognize. Submitting stores a User row with
 * status PENDING — no session is created here.
 */
export function RequestAccessPage() {
  const { isAuthenticated, requestAccess, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as RequestAccessState | null) ?? null;
  const [email, setEmail] = useState(state?.email ?? '');
  const [sources, setSources] = useState<Set<UserSource>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const toggleSource = (value: UserSource) => {
    setSources((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await requestAccess({ email, sources: Array.from(sources) });
      setSubmitted(true);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4 }}>
          {submitted ? (
            <Stack spacing={2} sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="primary" fontWeight={700}>
                Request received
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thanks — we've let our team know you'd like access. We'll be in touch at {email}.
              </Typography>
              <Link component={RouterLink} to="/login">
                Back to sign in
              </Link>
            </Stack>
          ) : (
            <>
              <Stack spacing={1} sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h5" color="primary" fontWeight={700}>
                  Request access
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vibelin is invite-only right now. Tell us how to reach you and we'll follow up.
                </Typography>
              </Stack>

              <form onSubmit={onSubmit} noValidate>
                <Stack spacing={2}>
                  {error && <Alert severity="error">{error}</Alert>}
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    autoComplete="email"
                  />
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      How did you hear about Vibelin?
                    </Typography>
                    <FormGroup>
                      {SOURCE_OPTIONS.map((opt) => (
                        <FormControlLabel
                          key={opt.value}
                          control={
                            <Checkbox
                              checked={sources.has(opt.value)}
                              onChange={() => toggleSource(opt.value)}
                            />
                          }
                          label={opt.label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                  <Button type="submit" variant="contained" size="large" disabled={loading || !email}>
                    {loading ? 'Submitting…' : 'Request access'}
                  </Button>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    <Link component="button" type="button" onClick={() => navigate('/login')}>
                      Back to sign in
                    </Link>
                  </Typography>
                </Stack>
              </form>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
